import { API_V2 } from "@/src/services/api";

/**
 * A department is a top-level subcategory (portal-api `SubCategories.parentId IS NULL`);
 * its `children` are the leaves beneath it.
 *
 * Sellers whose catalogue mirrors their own site — Farm Fresh Bangalore, for
 * example — model their storefront nav as `platform category -> department -> leaf`
 * (Grocery -> Vegetables -> Leafy Greens). Products carry both the department and
 * the leaf in their flat `subcategories[]` array with no parent pointer, so the
 * hierarchy has to come from here.
 */
export interface SubcategoryNode {
  id: number;
  name: string;
  imageUrl?: string | null;
  description?: string | null;
  children?: SubcategoryNode[];
}

/**
 * Departments for one platform category, each with its leaves.
 *
 * Backed by `GET /api/v2/sub-categories/category/:categoryId`, which is public and
 * already returns the nested shape. Returns `[]` on any failure so a storefront
 * never breaks over a missing hierarchy — callers fall back to flat category pills.
 */
export async function getSubcategoryTree(categoryId: string | number): Promise<SubcategoryNode[]> {
  if (!API_V2 || categoryId === "" || categoryId == null) return [];
  try {
    const res = await fetch(`${API_V2.replace(/\/+$/, "")}/sub-categories/category/${categoryId}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return [];
    const body = await res.json();
    const data = body?.data;
    return Array.isArray(data) ? (data as SubcategoryNode[]) : [];
  } catch (err) {
    console.error("getSubcategoryTree error:", err);
    return [];
  }
}

/** Fetches several categories' trees in parallel, keyed by the category id as a string. */
export async function getSubcategoryTrees(
  categoryIds: (string | number)[]
): Promise<Map<string, SubcategoryNode[]>> {
  const unique = Array.from(new Set(categoryIds.map(String))).filter(Boolean);
  const results = await Promise.all(
    unique.map(async (id) => [id, await getSubcategoryTree(id)] as const)
  );
  return new Map(results);
}
